import {createContext, useState, ReactNode, useContext, FC} from 'react';
import AurDialog from '../../components/common/aur-dialog/AurDialog';

type ButtonStyle = 'single' | 'sideBySide' | undefined;

type ShowModalParameters = {
  title: string; // title text of the modal
  description: string; // description text of the modal
  labelCancelButton?: string; // label for Cancel button; No need to send value when buttonStyle = 'single'
  labelOkButton: string; // label for Ok button
  onOk?: () => void; // callback method for Ok button
  onCancel?: () => void; // callback method for Cancel button. If not sent, it will only hide the modal; ; No need to send value when buttonStyle = 'single'
  buttonStyle?: ButtonStyle; // decides the number and alignment style of the modal button(s)
  className?: string;
  cancelButtonTitle?: string;
};

// Define the type for the context, which includes the 'showModal' function
interface ModalContextType {
  showModal: (params: ShowModalParameters) => void;
}

// Create the ModalContext with an initial value of undefined
const ModalContext = createContext<ModalContextType | undefined>(undefined);

// Custom hook to access the ModalContext
export const useModal = (): ModalContextType => {
  const context = useContext(ModalContext);
  // Ensure that the hook is used within a ModalProvider
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

// Define the props for ModalProvider
type ModalProviderProps = {
  children: ReactNode;
};

export const ModalProvider: FC<ModalProviderProps> = ({children}) => {
  // State for modal visibility and content
  const [isVisible, setIsVisible] = useState(false); // Tracks if the modal is visible
  const [title, setTitle] = useState<string>(''); // Modal title
  const [description, setDescription] = useState<string>(''); // Modal description
  const [labelOkButton, setLabelOkButton] = useState<string>(''); // Label for OK button
  const [labelCancelButton, setLabelCancelButton] = useState<
    string | undefined
  >(''); // Label for Cancel button
  const [onOk, setOnOk] = useState<() => void>(); // Function to execute on OK
  const [onCancel, setOnCancel] = useState<() => void>(); // Function to execute on Cancel (optional)
  const [buttonStyle, setButtonStyle] = useState<ButtonStyle>(undefined); // Modal Button Style (optional)
  const [className, setClassName] = useState<string | undefined>(undefined);
  const [cancelButtonTitle, setCancelButtonTitle] = useState<string|undefined>(undefined);

  // Function to display the modal with the given parameters
  const showModal = (params: ShowModalParameters) => {
    setTitle(params.title); // Set modal title
    setDescription(params.description); // Set modal description
    setLabelOkButton(params.labelOkButton); // Set OK button label
    setLabelCancelButton(params.labelCancelButton); // Set Cancel button label
    setOnOk(() => params.onOk); // Set OK button handler
    setOnCancel(() => params.onCancel); // Set Cancel button handler (optional)
    setIsVisible(true); // Make modal visible
    setButtonStyle(params.buttonStyle);
    setClassName(params.className);
    setCancelButtonTitle(params.cancelButtonTitle);
  };

  // Function to hide the modal and reset its state
  const hideModal = () => {
    setIsVisible(false); // Hide modal
    setTitle(''); // Reset title
    setDescription(''); // Reset description
    setLabelOkButton(''); // Reset OK button label
    setLabelCancelButton(''); // Reset Cancel button label
    setOnOk(undefined); // Reset OK handler
    setOnCancel(undefined); // Reset Cancel handler
    setButtonStyle(undefined);
    setClassName(undefined);
    setCancelButtonTitle(undefined);
  };

  // Handler for OK button click, executes the 'onOk' callback and hides the modal
  const modalOkClickHandler = () => {
    if (onOk) onOk(); // Execute OK callback if it exists
    hideModal(); // Hide modal after action
  };

  // Handler for Cancel button click, executes the 'onCancel' callback and hides the modal
  const modalCancelClickHandler = () => {
    if (onCancel) onCancel(); // Execute Cancel callback if it exists
    hideModal(); // Hide modal after action
  };

  return (
    // Provide the 'showModal' function via context to child components
    <ModalContext.Provider value={{showModal}}>
      {children} {/* Render child components */}
      {/* Render the modal dialog if visible */}
      {isVisible && (
        <AurDialog
          popupTitle={title} // Pass the title to the modal
          popupContent={description} // Pass the description to the modal
          labelActionOk={labelOkButton} // Pass the OK button label
          labelActionCancel={labelCancelButton} // Pass the Cancel button label
          onOk={modalOkClickHandler} // Pass the OK handler
          onCancel={modalCancelClickHandler} // Pass the Cancel handler
          showDialog={isVisible} // Control modal visibility
          buttonStyle={buttonStyle}
          className={className}
          cancelButtonTitle={cancelButtonTitle} 
        />
      )}
    </ModalContext.Provider>
  );
};
